import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { callAuthenticatedFunction } from "@/lib/authUtils";

// Components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { InfoTooltip } from "@/components/ui/info-tooltip";

// Icons
import { Save, Loader2, Wand2 } from "lucide-react";

// Custom Icons
import jobIcon from "@/assets/jobs/icon_industry.svg";

// Hooks
import { useToast } from "@/hooks/use-toast";

// API & Types
import { getAllUsers } from "@/api/users";
import { UserRole, RecruitmentStatus, type User } from "@/lib/schema";

// Job Components
import { RecruitmentStatusSelector } from "./RecruitmentStatusSelector";

// Utils
import {
  type JobFormState,
  getInitialJobFormState,
  validateJobForm,
  handleSkillKeyDown,
  handleSkillBlur,
  JOB_STATUS_OPTIONS,
  REMOTE_TYPE_OPTIONS
} from "@/lib/jobFormUtils";
import { getJobSettings } from "@/api/jobSettings";
import { addSkillToList, removeSkillFromList } from "@/lib/jobUtils";

interface JobFormProps {
  initialData?: JobFormState;
  onSubmit: (formData: JobFormState, recruiters?: Array<{id: string, firstName: string, lastName: string}>) => Promise<boolean>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  title?: string;
  showHeader?: boolean;
}

export function JobForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitButtonText = "Create Job",
  title = "Job Details",
  showHeader = true
}: JobFormProps) {
  // Hooks
  const { toast } = useToast();

  // State
  const [recruiters, setRecruiters] = useState<User[]>([]);
  const [loadingRecruiters, setLoadingRecruiters] = useState(true);
  const [formData, setFormData] = useState<JobFormState>(
    initialData || getInitialJobFormState()
  );
  const [generatingSkills, setGeneratingSkills] = useState(false);
  const [refiningDescription, setRefiningDescription] = useState(false);
  const [jobOptions, setJobOptions] = useState({
    employmentTypes: [] as Array<{ value: string; label: string }>,
    categories: [] as Array<{ value: string; label: string }>,
    priorities: [] as Array<{ value: string; label: string }>
  });

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingRecruiters(true);
        
        // Fetch recruiters and job settings in parallel
        const [users, settings] = await Promise.all([
          getAllUsers([UserRole.RECRUITER]),
          getJobSettings()
        ]);
        
        setRecruiters(users);
        
        // Set job options from settings or fallback to defaults
        if (settings) {
          setJobOptions({
            employmentTypes: settings.employmentTypes
              .map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ') }))
              .sort((a, b) => a.label.localeCompare(b.label)),
            categories: settings.categories
              .map(cat => ({ value: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1) }))
              .sort((a, b) => a.label.localeCompare(b.label)),
            priorities: settings.priorities
              .map(pri => ({ value: pri, label: pri.charAt(0).toUpperCase() + pri.slice(1) }))
              .sort((a, b) => a.label.localeCompare(b.label))
          });
        } else {
          // Fallback to hardcoded values if no settings found
          const { EMPLOYMENT_TYPES, JOB_CATEGORIES, JOB_PRIORITY_OPTIONS } = await import("@/lib/jobFormUtils");
          setJobOptions({
            employmentTypes: [...EMPLOYMENT_TYPES].sort((a, b) => a.label.localeCompare(b.label)),
            categories: [...JOB_CATEGORIES].sort((a, b) => a.label.localeCompare(b.label)),
            priorities: [...JOB_PRIORITY_OPTIONS].sort((a, b) => a.label.localeCompare(b.label))
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // Fallback to hardcoded values on error
        const { EMPLOYMENT_TYPES, JOB_CATEGORIES, JOB_PRIORITY_OPTIONS } = await import("@/lib/jobFormUtils");
        setJobOptions({
          employmentTypes: [...EMPLOYMENT_TYPES].sort((a, b) => a.label.localeCompare(b.label)),
          categories: [...JOB_CATEGORIES].sort((a, b) => a.label.localeCompare(b.label)),
          priorities: [...JOB_PRIORITY_OPTIONS].sort((a, b) => a.label.localeCompare(b.label))
        });
      } finally {
        setLoadingRecruiters(false);
      }
    };

    fetchData();
  }, []);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Utility functions
  const handleInputChange = (field: keyof JobFormState, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTextareaRows = (text: string, minRows = 6, maxRows = 20) => {
    const lines = text.split('\n').length;
    const estimatedLines = Math.ceil(text.length / 80);
    const calculatedRows = Math.max(lines, estimatedLines);
    return Math.min(Math.max(calculatedRows, minRows), maxRows);
  };

  // Skill management functions
  const addSkillToJob = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skills: addSkillToList(prev.skills, skill)
    }));
  };

  const removeSkillFromJob = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: removeSkillFromList(prev.skills, skillToRemove)
    }));
  };

  const handleSkillsInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleSkillKeyDown(
      e,
      formData.skillsInput,
      addSkillToJob,
      () => setFormData(prev => ({ ...prev, skillsInput: "" }))
    );
  };

  const handleSkillsInputBlur = () => {
    handleSkillBlur(
      formData.skillsInput,
      addSkillToJob,
      () => setFormData(prev => ({ ...prev, skillsInput: "" }))
    );
  };

  // Validation
  const canGenerateSkills = formData.title.trim().length > 5 && formData.description.trim().length >= 50;
  const canRefineDescription = formData.description.trim().length >= 50;

  // AI Functions
  const handleGenerateSkills = async () => {
    if (formData.title.trim().length <= 5) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Job Title Too Short",
        description: "Job title must be more than 5 characters to generate skills.",
        variant: "destructive"
      });
      return;
    }

    if (formData.description.trim().length < 50) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Job Description Too Short", 
        description: "Job description must be at least 50 characters to generate skills.",
        variant: "destructive"
      });
      return;
    }

    setGeneratingSkills(true);
    try {
      const result = await callAuthenticatedFunction('generateSkills', {
        jobTitle: formData.title,
        jobDescription: formData.description
      }, 180000); // 3 minutes timeout for AI-powered skill generation
      
      const skills = result.data as string[];
      
      setFormData(prev => ({ ...prev, skills }));
      
      toast({
        title: "Skills Generated",
        description: `Generated ${skills.length} skills based on your job description.`
      });
    } catch (error) {
      console.error("Error generating skills:", error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to generate skills. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGeneratingSkills(false);
    }
  };

  // Refine job description using AI
  const handleRefineDescription = async () => {
    if (formData.description.trim().length < 50) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Description Too Short",
        description: "Job description must be at least 50 characters to refine.",
        variant: "destructive"
      });
      return;
    }

    setRefiningDescription(true);
    try {
      const result = await callAuthenticatedFunction('formatJobDescription', {
        jobDescription: formData.description
      }, 180000); // 3 minutes timeout for AI-powered job description formatting
      
      const { formattedDescription } = result.data as { formattedDescription: string };
      
      // Convert HTML to plain text for textarea (removing HTML tags)
      const plainText = formattedDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      
      setFormData(prev => ({ ...prev, description: plainText }));
      
      toast({
        title: "Description Refined",
        description: "Your job description has been optimized for the platform."
      });
    } catch (error) {
      console.error("Error refining description:", error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to refine description. Please try again.",
        variant: "destructive"
      });
    } finally {
      setRefiningDescription(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Pass recruiters in simplified format using uid
    const mappedRecruiters = recruiters.map(recruiter => ({
      id: recruiter.id, // This is the Firebase UID
      firstName: recruiter.firstName,
      lastName: recruiter.lastName
    }));
    const success = await onSubmit(formData, mappedRecruiters);
    if (success && !initialData) {
      // Reset form only if creating new job (not editing)
      setFormData(getInitialJobFormState());
    }
  };

  const formContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title" className="font-medium">Job Title *</Label>
          <Input 
            id="title" 
            name="title" 
            placeholder="Enter job title" 
            data-testid="job-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="jobId" className="font-medium">Job Id *</Label>
          <Input 
            id="jobId" 
            name="jobId" 
            placeholder="Enter Job Id (example: 0001)" 
            data-testid="job-id"
            value={formData.jobId}
            onChange={(e) => handleInputChange('jobId', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="employmentType" className="font-medium">Employment Type *</Label>
          <Select name="employmentType" value={formData.employmentType} onValueChange={(value) => handleInputChange('employmentType', value)}>
            <SelectTrigger id="employmentType" data-testid="job-employment-type">
              <SelectValue placeholder="Select employment type" />
            </SelectTrigger>
            <SelectContent
              className="z-[200]"
              position="popper"
              sideOffset={5}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {jobOptions.employmentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="category" className="font-medium">Category *</Label>
          <Select name="category" value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
            <SelectTrigger id="category" data-testid="job-category">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent
              className="z-[200]"
              position="popper"
              sideOffset={5}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {jobOptions.categories.map((category) => (
                <SelectItem key={category.value} value={category.value}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="location" className="font-medium">Location *</Label>
          <Input 
            id="location" 
            name="location" 
            placeholder="Enter Location" 
            data-testid="job-location"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="remoteType" className="font-medium">Remote Type *</Label>
          <Select name="remoteType" value={formData.remoteType} onValueChange={(value) => handleInputChange('remoteType', value)}>
            <SelectTrigger id="remoteType" data-testid="job-remote-type">
              <SelectValue placeholder="Select remote type" />
            </SelectTrigger>
            <SelectContent
              className="z-[200]"
              position="popper"
              sideOffset={5}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {REMOTE_TYPE_OPTIONS.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="salary" className="font-medium">Salary Range</Label>
          <Input 
            id="salary" 
            name="salary" 
            placeholder="e.g., $80,000 - $120,000 or Competitive" 
            data-testid="job-salary"
            value={formData.salary}
            onChange={(e) => handleInputChange('salary', e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Label htmlFor="rewardInAirPoints" className="font-medium">Referral Reward (AirPoints)</Label>
            <InfoTooltip 
              content="AirPoints are rewards given to successful referrers. Higher rewards attract more quality referrals."
              variant="info"
              size="sm"
              subtle
              side="right"
            />
          </div>
          <Input 
            id="rewardInAirPoints" 
            name="rewardInAirPoints" 
            type="number" 
            placeholder="Enter reward amount" 
            data-testid="job-reward-airpoints"
            value={formData.rewardInAirPoints}
            onChange={(e) => handleInputChange('rewardInAirPoints', e.target.value)}
          />
        </div>
      </div>

      {/* Recruitment Status Selector */}
      <RecruitmentStatusSelector
        value={formData.recruitmentStatus}
        onChange={(status) => handleInputChange('recruitmentStatus', status)}
        isEditMode={!!initialData}
      />

      <div>
        <Label htmlFor="description" className="font-medium">Description *</Label>
        <div className="space-y-2">
          <Textarea 
            id="description" 
            name="description" 
            placeholder="Enter the job description here..." 
            rows={calculateTextareaRows(formData.description)}
            data-testid="job-description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="resize-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400 italic">
              <Wand2 className="h-3 w-3" />
              <span>AI can optimize this for better engagement</span>
              {!canRefineDescription && (
                <span className="text-orange-500 font-medium">
                  (needs 50+ chars)
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRefineDescription}
              disabled={refiningDescription || !canRefineDescription}
              className="flex items-center gap-2 px-3"
              data-testid="refine-description-button"
            >
              {refiningDescription ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {refiningDescription ? 'Refining...' : 'Refine'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="status" className="font-medium">Status</Label>
          <Select name="status" value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
            <SelectTrigger id="status" data-testid="job-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className="z-[200]"
              position="popper"
              sideOffset={5}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {JOB_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="priority" className="font-medium">Priority</Label>
          <Select name="priority" value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
            <SelectTrigger id="priority" data-testid="job-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className="z-[200]"
              position="popper"
              sideOffset={5}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {jobOptions.priorities.map((priority) => (
                <SelectItem key={priority.value} value={priority.value}>
                  {priority.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Label htmlFor="skills" className="font-medium">Skills *</Label>
          <InfoTooltip 
            content="Skills will be displayed as tags and help candidates find relevant opportunities."
            variant="help"
            size="sm"
            subtle
            side="right"
          />
        </div>
        <div className="space-y-2">
          <Input
            id="skills"
            value={formData.skillsInput}
            onChange={(e) => setFormData(prev => ({ ...prev, skillsInput: e.target.value }))}
            onKeyDown={handleSkillsInputKeyDown}
            onBlur={handleSkillsInputBlur}
            placeholder="Type a skill and press Enter or comma to add"
            className=""
            data-testid="job-skills"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.skills.map((skill, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="flex items-center gap-1 px-2 py-1"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => removeSkillFromJob(skill)}
                  className="ml-1 text-xs hover:text-red-600"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400 italic">
              <Wand2 className="h-3 w-3" />
              <span>AI can generate relevant skills from job details</span>
              {!canGenerateSkills && (
                <span className="text-orange-500 font-medium">
                  (needs title 6+ & description 50+ chars)
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleGenerateSkills}
              disabled={generatingSkills || !canGenerateSkills}
              className="flex items-center gap-2 px-3"
              data-testid="generate-skills-button"
            >
              {generatingSkills ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {generatingSkills ? 'Generating...' : 'Generate'}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Press Enter or comma to add skills as tags
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Label htmlFor="importantNotes" className="font-medium">
            Important Notes / Role Requirements{!initialData && " *"}
          </Label>
          <InfoTooltip 
            content="Non-negotiable requirements referrers should know before submitting candidates (e.g., BEE compliance, specific degrees, visa requirements)"
            variant="help"
            size="sm"
            subtle
            side="right"
          />
        </div>
        <Textarea 
          id="importantNotes" 
          name="importantNotes" 
          placeholder={"e.g., Candidate must be BEE compliant\nRequires Computer Science degree\nMust have fintech industry experience"}
          rows={4}
          data-testid="job-important-notes"
          value={formData.importantNotes}
          onChange={(e) => handleInputChange('importantNotes', e.target.value)}
          className="resize-none font-manrope"
        />
        <p className="text-sm text-gray-500 mt-1">
          {!initialData ? "Required for new jobs. One requirement per line." : "Optional. One requirement per line."}
        </p>
      </div>

      <div>
        <Label htmlFor="assignedRecruiter" className="font-medium">Assigned Recruiter</Label>
        <Select name="assignedRecruiter" value={formData.assignedRecruiter} onValueChange={(value) => handleInputChange('assignedRecruiter', value)}>
          <SelectTrigger id="assignedRecruiter" data-testid="job-assigned-recruiter">
            <SelectValue placeholder={loadingRecruiters ? "Loading recruiters..." : "Select a recruiter (optional)"} />
          </SelectTrigger>
          <SelectContent
            className="z-[200]"
            position="popper"
            sideOffset={5}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {loadingRecruiters ? (
              <SelectItem value="loading" disabled>
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading recruiters...
                </div>
              </SelectItem>
            ) : recruiters.length > 0 ? (
              recruiters.map((recruiter) => (
                <SelectItem key={recruiter.id} value={recruiter.id}>
                  {recruiter.firstName} {recruiter.lastName}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="no-recruiters" disabled>
                No recruiters found
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button 
          variant="secondary" 
          size="md"
          data-testid="cancel-job"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          variant="primary"
          size="md"
          data-testid="save-job"
          onClick={handleSubmit}
          disabled={isSubmitting || !validateJobForm(formData, !initialData)}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {initialData ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {submitButtonText}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  if (showHeader) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src={jobIcon} alt="Job" className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {formContent}
        </CardContent>
      </Card>
    );
  }

  return formContent;
}